import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Chip, Box, Button,
  Typography,
  Card, CardContent, CardHeader, Grid, Collapse, IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LockIcon from '@mui/icons-material/Lock';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useActionSettings } from '../../hooks/useActionSettings';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import { useTranslation } from 'react-i18next';
import useStorageUpload from '../useStorageUpload';
import TagEditor from '../Widgets/TagEditor';

const SettingCreateDocxTemplate = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  
  // States
  const [targetPath, setTargetPath] = useState('');
  const [templatePath, setTemplatePath] = useState('');
  const [templateDisplayPath, setTemplateDisplayPath] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [documentPassword, setDocumentPassword] = useState('');
  
  // UI States
  const [templateBrowseDisabled, setTemplateBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Refs
  const targetRef = useRef(null);
  const templateRef = useRef(null);
  const passwordRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const normalizePath = useCallback((p) => (p || '').replace(/\\/g, '/'), []);

  const getDisplayPath = useCallback((absPath) => {
    if (!absPath) return '';
    if (restrictToUserFolder && allowedRoots && allowedRoots.length > 0) {
      const primaryRoot = normalizePath(allowedRoots[0]);
      const normalized = normalizePath(absPath);
      if (normalized === primaryRoot) return '/';
      if (normalized.startsWith(primaryRoot + '/')) {
        const rel = normalized.slice(primaryRoot.length);
        return rel ? `/${rel.replace(/^[\\/]+/, '')}` : '/';
      }
    }
    return absPath;
  }, [restrictToUserFolder, allowedRoots, normalizePath]);

  const resolveAbsolutePath = useCallback((incomingPath) => {
    if (!incomingPath) return '';

    const hasRestrict = restrictToUserFolder && allowedRoots && allowedRoots.length > 0;
    const primaryRoot = hasRestrict ? allowedRoots[0] : '';

    if (hasRestrict && typeof incomingPath === 'string') {
      const isRootRelative = incomingPath.startsWith('/') || incomingPath.startsWith('\\');
      const isDriveOrUNC = /^([A-Za-z]:[\\/]|\\\\)/.test(incomingPath);
      if (isRootRelative && !isDriveOrUNC) {
        const separator = primaryRoot.includes('\\') ? '\\' : '/';
        const trimmed = incomingPath.replace(/^[/\\]+/, '');
        return trimmed ? `${primaryRoot.replace(/[\\/]+$/, '')}${separator}${trimmed}` : primaryRoot;
      }
    }

    const pathIsAbsolute = /^([A-Za-z]:[\\/]|\\\\|\/)/.test(incomingPath);
    if (pathIsAbsolute || !hasRestrict) {
      return incomingPath;
    }

    const separator = primaryRoot.includes('\\') ? '\\' : '/';
    const trimmed = incomingPath.replace(/^[/\\]+/, '');
    return trimmed ? `${primaryRoot.replace(/[\\/]+$/, '')}${separator}${trimmed}` : primaryRoot;
  }, [restrictToUserFolder, allowedRoots]);

  const {
    uploading: templateUploadLoading,
    inputRef: uploadInputRef,
    handleFileChange: handleTemplateFileChange,
    triggerFileDialog: handleTemplateUploadClick
  } = useStorageUpload({
    command: 'uploadTemplateFile',
    caseid,
    accept: '.docx',
    successMessage: t('create_docx_template.upload_success', 'Template uploaded to storage'),
    errorMessage: t('create_docx_template.upload_error', 'Template upload failed'),
    sendingMessage: t('create_docx_template.uploading_template', 'Uploading template to storage...'),
    onSuccess: (data) => {
      if (data?.path) {
        const absPath = resolveAbsolutePath(data.path);
        setTemplatePath(absPath);
        setTemplateDisplayPath(getDisplayPath(absPath));
      }
    }
  });

  // Keep display path in sync with absolute path and restrictions
  useEffect(() => {
    if (!templatePath) {
      if (templateDisplayPath) setTemplateDisplayPath('');
      return;
    }
    const absPath = resolveAbsolutePath(templatePath);
    if (absPath !== templatePath) {
      setTemplatePath(absPath);
      return;
    }
    const display = getDisplayPath(absPath);
    if (display !== templateDisplayPath) {
      setTemplateDisplayPath(display);
    }
  }, [templatePath, templateDisplayPath, resolveAbsolutePath, getDisplayPath]);

  // Use unified action settings hook
  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'createdocxtemplate',
    onSaveStatusChange,
    customFields: {
      target: '/{filename}_template.docx',
      templatePath: '',
      selectedTags: [],
      documentPassword: ''
    },
    customComparison: (initial) => {
      const initialTargetDisplay = initial.target ? getDisplayPath(initial.target) : '';
      const initialTemplateDisplay = initial.templatePath ? getDisplayPath(initial.templatePath) : '';
      return (
        initialTargetDisplay === targetPath &&
        initialTemplateDisplay === templateDisplayPath &&
        JSON.stringify(initial.selectedTags || []) === JSON.stringify(selectedTags) &&
        (initial.documentPassword || '') === documentPassword
      );
    },
    customSaveData: () => ({
      target: targetPath,
      templatePath: templateDisplayPath,
      selectedTags,
      documentPassword: documentPassword
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (e) {
          console.error('Failed to parse data JSON:', e);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }
      
      if (parsedData.templatePath !== undefined) {
        const absPath = resolveAbsolutePath(parsedData.templatePath);
        setTemplatePath(absPath);
        setTemplateDisplayPath(getDisplayPath(absPath));
      }
      if (parsedData.selectedTags !== undefined) {
        setSelectedTags(parsedData.selectedTags);
      }
      if (parsedData.documentPassword !== undefined) {
        setDocumentPassword(parsedData.documentPassword);
      }
      if (parsedData.target) {
        setTargetPath(getDisplayPath(parsedData.target));
      }
    },
    enableConditions: true
  });

  const { handleBrowseDirectory: handleBrowseTemplateDirectory, FolderBrowserComponent: TemplateFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (selectedPath) => {
      const absPath = resolveAbsolutePath(selectedPath);
      setTemplatePath(absPath);
      setTemplateDisplayPath(getDisplayPath(absPath));
    },
    onFolderBrowserClose: () => {
      setTemplateBrowseDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (!folderPath) return;
      const absFolder = resolveAbsolutePath(folderPath);
      const displayFolder = getDisplayPath(absFolder);
      let folderPathClean = displayFolder.replace(/[\\/]+$/, '');
      const separator = folderPathClean.includes('\\') ? '\\' : '/';
      setTargetPath(`${folderPathClean}${separator}{filename}_template.docx`);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    }
  });

  const customDataTags = useMemo(() => [{ 
    key: 'tag_ai_generated_content', 
    label: 'AI Generated Content', 
    value: 'ai_generated_content', 
    description: 'Content generated by AI based on the prompt' 
  }], []);

  const availableTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) return [];
    return tags.map((tag) => ({
      key: tag.key || tag.value || tag.variable || tag.label,
      label: tag.label || tag.key || tag.value || tag.variable,
      value: tag.value || tag.variable || tag.key || tag.label,
      variable: tag.variable || tag.value || tag.key || tag.label,
      description: tag.description || '',
      type: tag.type || 'text',
      reply: tag.reply || '',
      tag_type: tag.tag_type || ''
    }));
  }, [tags]);

  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };
    
  const handleTagSelect = (value) => {
    if (menuType === 'target') {
      const selectedTag = availableTags.find(tag => tag.value === value);
      const tagType = selectedTag?.type || 'text';
      const insertValue = tagType === 'image' ? `${value}_image` : value;
      targetRef.current?.insertTag(insertValue);
    } else if (menuType === 'template') {
      templateRef.current?.insertTag(value);
    } else if (menuType === 'tags') {
      if (!selectedTags.includes(value)) {
        setSelectedTags([...selectedTags, value]);
      }
    } else if (menuType === 'password') {
      passwordRef.current?.insertTag(value);
    }
    handleMenuClose();
  };

  const handleTagRemove = useCallback((tagToRemove) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
  }, [selectedTags]);

  return (
    <ActionSettingsLayout
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      title={t('workflow.create_docx_template', 'Create Word Document from Template')}
      isSaveDisabled={isSaveDisabled}
      onSave={() => baseHandleSave()}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={availableTags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      tagsDropdownProps={{
        tags: availableTags,
        handleTagSelect: handleTagSelect,
        anchorEl: anchorEl,
        setAnchorEl: handleMenuClose,
        menuType: menuType,
        additionalTags: customDataTags
      }}
    >
      <input
        type="file"
        accept=".docx"
        ref={uploadInputRef}
        style={{ display: 'none' }}
        onChange={handleTemplateFileChange}
      />
      <Grid container spacing={3}>
        {/* Template Path */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    TEMPLATE
                  </Box>
                  {t('workflow.template_path', 'Template Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{
                mb: 1.5,
                border: '1px solid #d0d0e8',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#5757b0',
                },
                '&:focus-within': {
                  borderColor: '#5757b0',
                  boxShadow: '0 0 0 3px rgba(87, 87, 176, 0.1)',
                },
              }}>
                <Box sx={{ padding: '6px 8px' }}>
                  <TagEditor
                    ref={templateRef}
                    value={templateDisplayPath}
                    onChange={(value) => {
                      setTemplateDisplayPath(value);
                      setTemplatePath(resolveAbsolutePath(value));
                    }}
                    placeholder="C:\templates\template.docx"
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'template')}
                    tags={availableTags}
                  />
                </Box>
              </Box>

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTemplateBrowseDisabled(true);
                    handleBrowseTemplateDirectory();
                  }}
                  disabled={templateBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleTemplateUploadClick}
                  disabled={templateUploadLoading}
                  startIcon={<CloudUploadIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {templateUploadLoading ? t('create_docx_template.uploading', 'Uploading...') : t('create_docx_template.upload_template', 'Upload Template')}
                </Button>
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.template_path_help', 'Path to the DOCX template file with Jinja2 placeholders')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Target Path */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    OUTPUT
                  </Box>
                  {t('workflow.target_path', 'Target Path')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />              
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                ref={targetRef}
                value={targetPath}
                onChange={setTargetPath}
                placeholder={t('create_docx_template.target_path_placeholder', 'Enter target path with placeholders like {filename}_output.docx')}
                singleLine
                onTagClick={(e) => handleMenuOpen(e, 'target')}
                tags={availableTags}
              />

              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 1,
                mt: 1.5,
              }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowseDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Selected Tags */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Typography sx={{
                  fontWeight: 700,
                  color: '#444',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Box component="span" sx={{
                    bgcolor: '#5757b0',
                    color: 'white',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.7,
                    mr: { xs: 1, sm: 2 },
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    fontWeight: 600,
                    boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                  }}>
                    TAGS
                  </Box>
                  {t('workflow.selected_tags', 'Selected Tags for Template')}
                </Typography>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Box sx={{ mb: 2 }}>
                <Button
                  size="medium"
                  variant="outlined"
                  onClick={(e) => handleMenuOpen(e, 'tags')}
                  startIcon={<AddIcon />}
                  sx={{
                    color: '#5757b0',
                    borderColor: '#5757b030',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    mb: 1,
                  }}
                >
                  {t('workflow.add_tags', 'Add Tags')}
                </Button>
                {selectedTags.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {selectedTags.map((tagValue, index) => {
                      const tagObj = availableTags.find(tag => 
                        tag.variable === tagValue || tag.value === tagValue
                      );
                      const displayLabel = tagObj ? (tagObj.label || tagObj.variable || tagObj.value || tagObj.key) : tagValue;
                      const tagType = tagObj?.type || 'text';
                      
                      return (
                        <Chip
                          key={index}
                          label={displayLabel}
                          onDelete={() => handleTagRemove(tagValue)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          icon={tagType === 'image' ? <ImageIcon /> : undefined}
                          sx={tagType === 'image' ? {
                            backgroundColor: '#fff4e6',
                            borderColor: '#ff9800',
                            color: '#e65100',
                            '& .MuiChip-icon': { color: '#ff9800' }
                          } : undefined}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {t('workflow.selected_tags_help', 'These tags will be available as variables in your template. Use them like {{tag_name}} in your DOCX template.')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Advanced Options */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            border: '1px solid #5757b010',
            overflow: 'hidden',
          }}>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{
                    fontWeight: 700,
                    color: '#444',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Box component="span" sx={{
                      bgcolor: '#5757b0',
                      color: 'white',
                      borderRadius: '6px',
                      px: 1.5,
                      py: 0.7,
                      mr: { xs: 1, sm: 2 },
                      fontSize: '0.85rem',
                      letterSpacing: '0.5px',
                      fontWeight: 600,
                      boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)',
                    }}>
                      ADVANCED
                    </Box>
                    {t('workflow.advanced_options', 'Advanced Options')}
                  </Typography>
                  <IconButton
                    onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                  >
                    {showAdvancedOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              }
              sx={{
                borderBottom: '1px solid #f0f0f8',
                backgroundColor: '#fafbff',
                p: { xs: 1.5, sm: 2.5 },
              }}
            />
            <Collapse in={showAdvancedOptions}>
              <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
                {/* Document Password */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ 
                    mb: 1, 
                    fontWeight: 600, 
                    color: '#555',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <LockIcon sx={{ mr: 1, fontSize: '1rem' }} />
                    {t('workflow.document_password', 'Document Password')}
                  </Typography>
                  
                  <TagEditor
                    ref={passwordRef}
                    value={documentPassword}
                    onChange={setDocumentPassword}
                    placeholder={t('workflow.document_password_placeholder', 'Enter password with tags like {case_id} (optional)')}
                    singleLine
                    onTagClick={(e) => handleMenuOpen(e, 'password')}
                    tags={availableTags}
                  />
                  
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    {t('workflow.document_password_help', 'Password can include tags like {case_id} or {current_date}. Leave empty for no password protection.')}
                  </Typography>
                </Box>
              </CardContent>
            </Collapse>
          </Card>
        </Grid>
      </Grid>

      {TemplateFolderBrowser}
      {TargetFolderBrowser}
    </ActionSettingsLayout>
  );
};

export default React.memo(SettingCreateDocxTemplate);
